import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import moment from 'moment';
import reduce from 'lodash/reduce';
import set from 'lodash/set';

import Ivr from 'src/models/Ivrs/Ivr';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import {
  DATE_FIELD,
  TYPE_FIELD,
  TIME_RANGES_FOR_DATE_FIELD,
  LOCATION_FIELD,
} from 'src/presentation/components/pages/ivr/constants/scheduleConstants';
import { IvrNode } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import { NODE_LINKS_MAPPER } from 'src/models/Ivrs/NodeListMapper';
import { STORED_DATETIME_FORMAT } from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import { ScheduleType } from 'src/models/Ivrs/Nodes/ScheduleNodeDef';

export const urlHashToObj = () =>
  window.location.hash
    .substr(1)
    .split('&')
    .reduce((acc, val) => {
      const v = val.split('=');
      if (v.length === 2) {
        acc[v[0]] = v[1];
      }
      return acc;
    }, {});

export const getNodeListMap = (nodesArray: IvrNode[]) =>
  reduce(
    nodesArray,
    (acc: { [key: string]: any }, node: any) => {
      const id = get(node, 'ivrNode.id', '');
      acc[id] = node;
      return acc;
    },
    {}
  );

// deletes an array item 'itemIndex' without mutating the array
export const immutablyDeleteArrayItem = (arr = [], itemIndex) => arr.filter((_, idx) => idx !== itemIndex);

// deletes a node and resets all references to it from other nodes
// note: it mutates the nodes in the config
export function deleteNodeFromTree(config: Ivr | {} | undefined, nodeId: string | undefined) {
  const nodes = get(config, 'nodes', []);
  const nodeIndex = findIndex(nodes, (node: IvrNode) => get(node, 'ivrNode.id') === nodeId);

  if (isEmpty(config) || !nodeId || nodeIndex === -1) {
    return;
  }

  nodes.splice(nodeIndex, 1);

  forEach(nodes, node => {
    const currentNodeType = get(node, 'type');

    if (NODE_LINKS_MAPPER[currentNodeType]) {
      forEach(NODE_LINKS_MAPPER[currentNodeType], attr => {
        if (isObject(attr)) {
          // complex 'attr' defines a path to options (prefixAttr)
          // and the relative path within the option (relativeAttr)
          const prefixAttr = get(attr, 'prefixPath', '');
          const relativeAttr = get(attr, 'postfixPath');
          const options = get(node, `ivrNode.${prefixAttr}`, []);

          forEach(options, (opt, idx) => {
            const fullPathToLink = `ivrNode.${prefixAttr}[${idx}].${relativeAttr}`;
            const nodeLinkValue = get(node, fullPathToLink);

            if (nodeLinkValue === nodeId) {
              set(node, fullPathToLink, '');
            }
          });
        } else {
          const pathToLink = `ivrNode.${attr}`;
          const nodeLinkValue = get(node, pathToLink);
          if (nodeLinkValue === nodeId) {
            set(node, pathToLink, '');
          }
        }
      });
    }
  });
}

export const isTemporarySplitNode = (nodeId: string = ''): boolean => nodeId.startsWith('EDITABLE_');

export const isScheduleNode = node =>
  get(node, 'type') === NODE_TYPES.IVR_NODE_SCHEDULE && !isTemporarySplitNode(get(node, 'ivrNode.id', ''));

export const isOutdatedSchedule = schedule => {
  const scheduleType = get(schedule, TYPE_FIELD);
  const isDaySchedule = scheduleType === ScheduleType.SCHEDULE_TYPE_DATE;
  const date = get(schedule, DATE_FIELD);
  const timeRanges = get(schedule, TIME_RANGES_FOR_DATE_FIELD);

  if (!isDaySchedule || !date || isEmpty(timeRanges)) {
    return false;
  }

  const location = get(schedule, LOCATION_FIELD);
  const nowWithTZ = moment().tz(location);

  const eachTimeRangeIsBeforeNow = timeRanges.every(
    timeRange =>
      has(timeRange, 'end') &&
      nowWithTZ.diff(moment.tz(`${date} ${timeRange.end}`, STORED_DATETIME_FORMAT, location), 'minutes') > 0
  );

  return eachTimeRangeIsBeforeNow;
};
